import { useInView } from '../../hooks/useInView'
import wildberries from '../../assets/partners/wildberries.svg'
import ozon from '../../assets/partners/ozon.svg'
import magnit from '../../assets/partners/magnit.png'
import detskiyMir from '../../assets/partners/detskiy-mir.png'
import kuper from '../../assets/partners/kuper.svg'

interface PartnerLogo {
  src: string
  alt: string
  width: number
  height: number
  direct?: boolean
}

// Чтобы добавить логотип: положить монохромный SVG/PNG с альфа-каналом
// в src/assets/partners/, импортировать и дописать строку сюда.
// Серый цвет даёт mask-image в <Logo>, красить сам файл не нужно.
// Ждут файлов: Пятёрочка, Альфа-Банк, Т-Банк, Сбер, AliExpress.
const PARTNERS: PartnerLogo[] = [
  { src: wildberries, alt: 'Wildberries', width: 188, height: 33, direct: true },
  // Формально ниже соседей (24 против 30–33): логотип целиком из жирных капсов,
  // на равной высоте он выглядел бы тяжелее строчных соседей.
  { src: ozon, alt: 'Ozon', width: 110, height: 24 },
  { src: magnit, alt: 'Магнит', width: 173, height: 32 },
  { src: detskiyMir, alt: 'Детский мир', width: 162, height: 46 },
  { src: kuper, alt: 'Купер', width: 168, height: 30 },
]

const LOGO_COLOR = '#9095b0'

function fadeUp(inView: boolean, delay: number) {
  return {
    opacity: inView ? 1 : 0,
    transform: inView ? 'translateY(0)' : 'translateY(24px)',
    transition: `opacity 0.65s cubic-bezier(0.45,0,0.15,1) ${delay}ms, transform 0.65s cubic-bezier(0.45,0,0.15,1) ${delay}ms`,
  }
}

function Logo({ p }: { p: PartnerLogo }) {
  if (p.direct) {
    return (
      <img
        src={p.src}
        alt={p.alt}
        width={p.width}
        height={p.height}
        style={{ display: 'block', width: p.width, height: p.height, flexShrink: 0 }}
      />
    )
  }
  return (
    <div
      aria-label={p.alt}
      role="img"
      style={{
        width: p.width,
        height: p.height,
        flexShrink: 0,
        backgroundColor: LOGO_COLOR,
        // Кавычки обязательны: мелкие svg (<4 КБ) Vite инлайнит в data-URI
        // с одинарными кавычками внутри — без них url(...) невалиден и маска гаснет.
        WebkitMaskImage: `url("${p.src}")`,
        maskImage: `url("${p.src}")`,
        WebkitMaskSize: 'contain',
        maskSize: 'contain',
        WebkitMaskPosition: 'center',
        maskPosition: 'center',
        WebkitMaskRepeat: 'no-repeat',
        maskRepeat: 'no-repeat',
      }}
    />
  )
}

export function Partners() {
  const { ref, inView } = useInView(0.5, '0px 0px -80px 0px')
  return (
    <section className="mx-auto flex max-w-[1920px] flex-col items-center gap-4 py-12 sm:gap-10 sm:py-24 lg:px-[7%] lg:py-28">
      <p
        ref={ref}
        className="font-body px-4 text-center text-[16px] leading-[24px] text-[#676b94] sm:px-6 sm:text-[20px] sm:leading-[30px]"
        style={fadeUp(inView, 0)}
      >
        Доступ к&nbsp;аудитории лидеров рынка
      </p>

      {/* Mobile / tablet — swipe slider */}
      <div
        className="w-full overflow-x-auto min-[1200px]:hidden"
        style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' } as React.CSSProperties}
      >
        <div className="flex snap-x snap-mandatory items-center gap-10 px-8 sm:gap-14 sm:px-12">
          {PARTNERS.map((p, i) => (
            <div
              key={p.alt}
              className="flex snap-center flex-none items-center justify-center py-6"
              style={fadeUp(inView, 100 + i * 80)}
            >
              <Logo p={p} />
            </div>
          ))}
        </div>
      </div>

      {/* Desktop — flex row */}
      <div className="hidden w-full flex-nowrap items-center justify-between gap-x-8 min-[1200px]:flex">
        {PARTNERS.map((p, i) => (
          <div
            key={p.alt}
            className="flex items-center justify-center"
            style={{ padding: '27.75px 0', ...fadeUp(inView, 100 + i * 80) }}
          >
            <Logo p={p} />
          </div>
        ))}
      </div>

      <div
        className="h-px w-full max-w-[1000px]"
        style={{
          background:
            'radial-gradient(ellipse 500px 1px at center, rgba(149,149,155,0.4) 0%, rgba(230,230,230,0) 100%)',
          ...fadeUp(inView, 500),
        }}
      />
    </section>
  )
}
